import { useEffect, useRef, useSyncExternalStore } from 'react';

// ── Why this exists ─────────────────────────────────────────────────────────
// In Chrome / Safari / other mobile browsers the MetaMask connector talks to
// the MetaMask app through a relay. Before it can send the user to the app it
// loads its code, opens the relay and creates a session, and only then opens
// the metamask:// link. Mobile browsers only allow opening another app for a
// few seconds after a tap; on a slow connection that window is gone, the
// browser blocks the link without any message and "Connect" spins until the
// timeout. The same happens for "Send" (transaction) and "Switch network".
//
// The connector hands us the link (mobile.preferredOpenLink in wagmiConfig).
//
//   - CONNECT: never opened automatically. The connect dialog turns its button
//     into "Continue to MetaMask" and the user taps it. A tap is always
//     allowed, so this works the same every time (no blocked switch, no
//     Chrome "continue to app" prompt that only appears sometimes).
//   - Later approvals (Send, Switch network): opened automatically when the
//     tap is still recent, with OpenMetaMaskDialog as the fallback.
//
// Where that button appears:
//   - while connecting: inside the existing "Connect with MetaMask" dialog
//     (MetaMaskRequiredDialog), so connecting only ever uses ONE dialog;
//   - for later approvals (Send, Switch network): in OpenMetaMaskDialog.

let pendingLink = null;
let leftPageForLink = false;
let cancelHandler = null;

const linkListeners = new Set();
const hostListeners = new Set();
const hosts = [];

function emitLink() {
  linkListeners.forEach((listener) => listener());
}

function emitHosts() {
  hostListeners.forEach((listener) => listener());
}

// Chrome (and Safari 16.4+) report whether the last tap is still recent
// enough to open another app. Older browsers do not, so we just try.
function hasRecentTap() {
  if (typeof navigator === 'undefined') return false;

  const activation = navigator.userActivation;

  if (!activation) return true;

  return Boolean(activation.isActive);
}

function tryOpen(url) {
  if (typeof window === 'undefined') return;

  try {
    window.location.href = url;
  } catch {
    // The button in the prompt is the fallback.
  }
}

export function openMetaMaskLink(url) {
  if (!url) return;

  pendingLink = url;
  leftPageForLink = false;
  emitLink();

  // A connect is in progress: the dialog's "Continue to MetaMask" button opens it.
  if (typeof cancelHandler === 'function') return;

  if (hasRecentTap()) {
    tryOpen(url);
  }
}

export function getPendingMetaMaskLink() {
  return pendingLink;
}

export function clearPendingMetaMaskLink() {
  if (!pendingLink) return;

  pendingLink = null;
  leftPageForLink = false;
  emitLink();
}

// The connect flow registers how to abort itself while it runs, so closing
// the dialog stops the spinner instead of waiting for the timeout. A handler
// being set also means "a connect is in progress".
export function setMetaMaskCancelHandler(handler) {
  cancelHandler = handler;

  // The connect ended (connected, failed, cancelled or timed out): its link is
  // useless now and must not show up later as a Send/Switch prompt.
  if (typeof handler !== 'function') {
    pendingLink = null;
    leftPageForLink = false;
  }

  emitLink();
}

export function isMetaMaskConnectActive() {
  return typeof cancelHandler === 'function';
}

export function cancelMetaMaskPrompt() {
  const handler = cancelHandler;

  cancelHandler = null;
  clearPendingMetaMaskLink();
  emitLink();

  if (typeof handler === 'function') {
    handler();
  }
}

// When MetaMask opened (by the automatic attempt or the button) this page
// goes to the background. Coming back means a later approval (Send, Switch
// network) was handled in the app, so that prompt is no longer needed.
//
// During a CONNECT the link is kept: MetaMask sometimes opens without showing
// the request. Back on the page, "Open MetaMask" must still be there so the
// user can send the same request again (the link carries the whole request).
// It is cleared when the connect ends (connected, cancelled or timed out).
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!pendingLink) return;

    if (document.visibilityState === 'hidden') {
      leftPageForLink = true;
      return;
    }

    if (!leftPageForLink) return;
    if (typeof cancelHandler === 'function') return;

    clearPendingMetaMaskLink();
  });
}

function subscribeLink(listener) {
  linkListeners.add(listener);

  return () => {
    linkListeners.delete(listener);
  };
}

export function useMetaMaskOpenLink() {
  return useSyncExternalStore(subscribeLink, getPendingMetaMaskLink, getPendingMetaMaskLink);
}

export function useMetaMaskConnectActive() {
  return useSyncExternalStore(subscribeLink, isMetaMaskConnectActive, isMetaMaskConnectActive);
}

// The connect button is rendered in several places (header, transfer page,
// LP page). Only the first one mounted shows the approval prompt, so it never
// appears twice.
function subscribeHosts(listener) {
  hostListeners.add(listener);

  return () => {
    hostListeners.delete(listener);
  };
}

function getFirstHost() {
  if (hosts.length === 0) return null;
  return hosts[0];
}

export function useIsMetaMaskPromptHost() {
  const idRef = useRef(null);

  if (!idRef.current) {
    idRef.current = {};
  }

  useEffect(() => {
    const id = idRef.current;

    hosts.push(id);
    emitHosts();

    return () => {
      const index = hosts.indexOf(id);

      if (index >= 0) {
        hosts.splice(index, 1);
      }

      emitHosts();
    };
  }, []);

  const firstHost = useSyncExternalStore(subscribeHosts, getFirstHost, getFirstHost);

  return firstHost === idRef.current;
}
